if (!customElements.get("td-video-modal")) {
  class tdVideoModal extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.initializeModal();
    }

    initializeModal() {
      // Once-only: re-parent the modal to <body> so it wins the document-order
      // tiebreak against other body-level popups at z-index: 2147483647.
      // appendChild fires disconnect+connect synchronously, which re-enters
      // this method — the flag short-circuits to avoid double-binding listeners.
      if (!this._didMoveToBody) {
        this._didMoveToBody = true;
        if (this.parentElement !== document.body) {
          const overlay = document.querySelector('.td-video-modal__overlay');
          if (overlay && overlay.parentElement !== document.body) {
            document.body.appendChild(overlay);
          }
          document.body.appendChild(this);
          // The recursive connectedCallback handles full setup; bail here.
          return;
        }
      }

      this.activeModalVideos = null;
      this.activeModal = null;
      this.volumeButton = this.querySelector('[volume-trigger]');
      this.modalOverlay = document.querySelector('.td-video-modal__overlay');
      this.slider = this.querySelector('swiper-slider');
      this.modalButtons = document.querySelectorAll('[modal-trigger]');
      this.reelTriggerButtons = document.querySelectorAll('.td-reel-trigger-button[data-td-reels-scroll-guard]');

      this.bindReelTouchGuards();
      this.bindModalTriggers();
      this.bindOutsideClickToClose();
      this.bindVolumeButton();
    }

    async bindModalTriggers() {
      if (typeof customElements !== 'undefined' && customElements.whenDefined) {
        await customElements.whenDefined('swiper-slider');
      }
      this.modalButtons.forEach((button) => {
        button.addEventListener('click', (event) => this.handleModalTriggerClick(event, button));
      });
    }

    handleModalTriggerClick(event, button) {
      // Trigger buttons inside the horizontal-scrolling reel row have a touch
      // guard that flips `data-reels-touch-moved=true` when the user is
      // scrolling rather than tapping. Swallow the click in that case so the
      // row scrolls without opening the modal.
      if (this.shouldSwallowTouchScrollClick(button)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      event.stopPropagation();

      const targetIndex = this.findSlideIndexForTrigger(button);
      this.activeModal = document.getElementById(button.getAttribute('modal-id'));

      if (this.activeModal && this.activeModal.hasAttribute('open')) {
        this.close(this.activeModal);
      } else {
        this.open(this.activeModal, targetIndex);
      }

      this.scrollToTargetSlide(targetIndex);
      this.focusModalAfterTransition(this.activeModal);
    }

    shouldSwallowTouchScrollClick(button) {
      if (!button.matches('[data-td-reels-scroll-guard]')) return false;
      if (button.dataset.reelsTouchMoved !== 'true') return false;
      button.dataset.reelsTouchMoved = 'false';
      return true;
    }

    findSlideIndexForTrigger(button) {
      const targetId = button.getAttribute('target');
      if (!targetId || !this.slider || !this.slider.slides) return null;
      const selectedSlide = this.slider.querySelector(`#${targetId}`);
      if (!selectedSlide) return null;
      return Array.from(this.slider.slides).findIndex((slide) => slide.id === selectedSlide.id);
    }

    focusModalAfterTransition(modal) {
      if (!modal) return;
      // The modal's transform/opacity transition is ~300ms (see td-reels.css).
      // Focus too early and the browser ignores it for elements not yet visible.
      setTimeout(() => {
        const firstFocusable = modal.querySelector('button');
        if (firstFocusable) firstFocusable.focus();
      }, 300);
    }

    bindOutsideClickToClose() {
      document.addEventListener('click', (event) => this.handleOutsideClick(event));
    }

    handleOutsideClick(event) {
      if (!this.activeModal || this.activeModal.contains(event.target)) return;
      if (this.isVolumeButtonClick(event)) return;
      if (event.target.closest('[modal-trigger]')) return;
      this.close(this.activeModal);
    }

    isVolumeButtonClick(event) {
      if (!this.volumeButton) return false;
      return this.volumeButton === event.target || this.volumeButton.contains(event.target);
    }

    bindVolumeButton() {
      if (!this.volumeButton) {
        console.warn("Volume button is not found or not initialized.");
        return;
      }
      this.volumeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggleVolume();
      });
    }

    bindReelTouchGuards() {
      this.reelTriggerButtons.forEach((button) => {
        if (button.dataset.reelsTouchGuardInitialized === 'true') {
          return;
        }

        let startX = 0;
        let startY = 0;

        button.dataset.reelsTouchGuardInitialized = 'true';
        button.dataset.reelsTouchMoved = 'false';

        button.addEventListener('touchstart', (event) => {
          const touch = event.touches[0];

          if (!touch) {
            return;
          }

          startX = touch.pageX;
          startY = touch.pageY;
          button.dataset.reelsTouchMoved = 'false';
        }, { passive: true });

        button.addEventListener('touchmove', (event) => {
          const touch = event.touches[0];

          if (!touch) {
            return;
          }

          if (Math.abs(touch.pageX - startX) > 8 || Math.abs(touch.pageY - startY) > 8) {
            button.dataset.reelsTouchMoved = 'true';
          }
        }, { passive: true });

        button.addEventListener('touchcancel', () => {
          button.dataset.reelsTouchMoved = 'false';
        }, { passive: true });
      });
    }

    open(modal, targetIndex) {
      this.activeModalVideos = modal.querySelectorAll('video');
      document.body.classList.add('scroll-hidden--mobile');
      this.modalOverlay.classList.add('td-video-modal__overlay--active');

      const idx = targetIndex == null ? 0 : targetIndex;

      if (modal) {
        // Pre-sync --active-slide-w and --active-aspect to the target
        // video's intrinsic dimensions BEFORE showing the modal — without
        // this, the previous open's values linger and the user briefly
        // sees the modal at old dimensions before swiper-slider.js's
        // slideChange handler corrects them.
        const sliderEl = modal.querySelector('swiper-slider');
        const targetSlide = sliderEl && sliderEl.slides ? sliderEl.slides[idx] : null;
        const targetVideo = targetSlide && targetSlide.querySelector('video');
        if (targetVideo && targetVideo.videoWidth && targetVideo.videoHeight) {
          const w = Math.round(500 * targetVideo.videoWidth / targetVideo.videoHeight);
          modal.style.setProperty('--active-slide-w', `${w}px`);
          modal.style.setProperty('--active-aspect', `${targetVideo.videoWidth} / ${targetVideo.videoHeight}`);
        } else {
          modal.style.removeProperty('--active-slide-w');
          modal.style.removeProperty('--active-aspect');
        }
        modal.setAttribute('open', '');
      }

      // Play first (videos are rendered muted in liquid → autoplay allowed),
      // then restore the last user-chosen volume state. The volume button's
      // `muted` attribute persists across open/close cycles, so reopening
      // remembers whether the user muted/unmuted the previous session.
      this.playVideo(idx);
      const isMuted = this.volumeButton && this.volumeButton.getAttribute('muted') === 'true';
      setTimeout(() => this.setVideosVolume(isMuted ? 0 : 0.25), 200);
    }

    scrollToTargetSlide(index) {
      this.slider.goToSlide(index);
    }

    async playVideo(videoIndex) {
      if(this.activeModalVideos[videoIndex]) {
        try {
          // Store the play promise
          const playPromise = this.activeModalVideos[videoIndex].play();
          
          // If the browser supports promises, wait for it to complete
          if (playPromise !== undefined) {
            await playPromise;
            // Now it's safe to pause if needed
          }
        } catch (error) {
          console.warn('Error playing video:', error);
        }
      }
    }

    toggleVolume() {
      if (this.volumeButton) {
        this.volumeButton.setAttribute('muted', `${this.volumeButton.getAttribute('muted') == 'false' ? true : false}`); 
          
        if(this.volumeButton.getAttribute('muted') == 'true') {
          this.volumeButton.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.508.867C12.313.79 12.156.79 12 .79c-.313 0-.625.078-.86.313L5.868 5.75H2.625C1.57 5.75.75 6.61.75 7.625v3.71c0 1.017.82 1.837 1.875 1.837h3.242l5.274 4.687c.234.196.546.313.859.313.156 0 .313-.04.508-.117.43-.196.703-.664.703-1.133V2c0-.469-.274-.938-.703-1.133Zm-1.133 14.766-4.766-4.258H2.625v-3.71h3.984l4.766-4.259v12.227ZM20.789 9.5l1.836-1.836c.39-.39.39-.976 0-1.328-.352-.39-.938-.39-1.328 0L19.5 8.21l-1.875-1.875c-.39-.352-.977-.352-1.328 0-.39.39-.39.976 0 1.328L18.133 9.5l-1.836 1.836c-.39.39-.39.976 0 1.328.351.39.937.39 1.328 0l1.875-1.836 1.836 1.836c.352.39.937.39 1.289 0 .39-.351.39-.937 0-1.328L20.789 9.5Z" fill="#fff"></path></svg>';
          this.setVideosVolume(0);
        } else {
          this.volumeButton.innerHTML = '<svg width="20" height="17" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.758.867A1.349 1.349 0 0 0 11.25.75c-.313 0-.625.117-.86.352L5.118 5.75H1.875C.82 5.75 0 6.61 0 7.625v3.71c0 1.017.82 1.837 1.875 1.837h3.242l5.274 4.687c.234.313.546.391.859.391.156 0 .313 0 .508-.078.43-.235.742-.664.742-1.172V2c0-.469-.313-.938-.742-1.133Zm-1.133 14.766-4.766-4.258H1.875v-3.71h3.984l4.766-4.259v12.227Zm5.469-9.024a1.053 1.053 0 0 0-.586-.195.962.962 0 0 0-.742.352.942.942 0 0 0 .156 1.328c.43.351.703.86.703 1.406a1.85 1.85 0 0 1-.703 1.445.942.942 0 0 0-.156 1.328.962.962 0 0 0 .742.352c.195 0 .43-.078.586-.195.898-.742 1.406-1.797 1.406-2.93 0-1.094-.508-2.148-1.406-2.89Zm2.383-2.89c-.196-.117-.391-.196-.625-.196-.274 0-.508.118-.704.352-.351.39-.273.977.118 1.328 1.328 1.055 2.109 2.656 2.109 4.297 0 1.68-.781 3.281-2.11 4.336-.39.351-.468.937-.117 1.328.196.234.43.352.704.352.234 0 .43-.079.625-.196 1.757-1.445 2.773-3.554 2.773-5.82 0-2.227-1.016-4.336-2.773-5.781Z" fill="#fff"></path></svg>';
          this.setVideosVolume(.25);
        }
      }
    }

    async pauseAllVideos() {
      if (Array.from(this.activeModalVideos).length) {
        for (const video of this.activeModalVideos) {
          if (video && !video.paused) {
            try {
              // Check if the video is actually playing
              if (!video.paused && !video.ended && video.currentTime > 0) {
                video.pause();
              }
            } catch (error) {
              console.warn('Error pausing video:', error);
            }
          }
        }
      }
    }

    setVideosVolume(newVolume) {
      if (this.activeModalVideos) {
        this.activeModalVideos.forEach(video => {
          if (newVolume > 0) {
            video.muted = false;
            video.volume = newVolume;
          } else {
            video.muted = true;
            video.volume = newVolume;
          }
        });
      }
    }

    close(modal) {
      document.body.classList.remove('scroll-hidden--mobile');
      this.modalOverlay.classList.remove('td-video-modal__overlay--active');
      if (modal) {
        modal.removeAttribute('open');
      }
      this.pauseAllVideos();
      this.setVideosVolume(0);
    }
  }

  customElements.define('td-video-modal', tdVideoModal);
}
